import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './Bank.css';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import OrderDesign from './OrderDesign';
import { useContainerScale } from './hooks/useViewportScale';
import { TutorialMessageBox } from './tutorial';

// Pricing based on rarity
const RARITY_PRICES = {
  common: 3000,
  rare: 6000,
  epic: 10000,
  legendary: 30000
};

// Feature images - same as Profile.js
const preparationBonusFeatures = [
  { key: 'plan_ahead', image: '/images/plan_ahead.jpg', alt: 'Plan Ahead' },
  { key: 'black_market', image: '/images/black_market_dossier.jpg', alt: 'Black Market Dossier' },
  { key: 'team_leader', image: '/images/team_leader_briefing.jpg', alt: 'Team Leader Briefing' }
];

const diplomaticAccordsFeatures = [
  { key: 'strategic_coordination', image: '/images/strategic_coordination.jpg', alt: 'Strategic Coordination' },
  { key: 'public_relations', image: '/images/public_relations.jpg', alt: 'Public Relations' },
  { key: 'emergency_protocol', image: '/images/emergency_protocol.jpg', alt: 'Emergency Protocol' },
  { key: 'command_directive', image: '/images/command_directive.jpg', alt: 'Command Directive' }
];

function Bank({ user, onLogout }) {
  const navigate = useNavigate();
  const { t, i18n: i18nInstance } = useTranslation();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [billyBucks, setBillyBucks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseMessage, setPurchaseMessage] = useState(null);
  const [gambling, setGambling] = useState(false);
  const [gambleResult, setGambleResult] = useState(null);
  const [purchaseMode, setPurchaseMode] = useState('byOrder'); // 'byOrder' or 'completeStrategy'
  const [incompleteStrategies, setIncompleteStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [missingOrders, setMissingOrders] = useState([]);
  const [loadingStrategies, setLoadingStrategies] = useState(false);
  const [orderDuplicators, setOrderDuplicators] = useState({
    common: 0,
    rare: 0,
    epic: 0,
    legendary: 0
  });
  const [converting, setConverting] = useState(false);
  const [convertMessage, setConvertMessage] = useState(null);
  const [showTutorialBank, setShowTutorialBank] = useState(false);
  const [seenTutorialMessages, setSeenTutorialMessages] = useState([]);
  
  // Play bank music (using same as profile for now)
  useMusic('/music/rock_4.mp3');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/tutorial/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) setSeenTutorialMessages(data.seenMessages || []);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user && !loading && !seenTutorialMessages.includes('bank')) {
      const timer = setTimeout(() => setShowTutorialBank(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, loading, seenTutorialMessages]);

  // Fetch all orders and Billy Bucks balance
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No token found');
          return;
        }

        // Fetch orders
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const ordersRes = await fetch(`${API_URL}/bank/orders?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': normalizedLanguage,
          },
        });

        if (ordersRes.ok) {
          const ordersData = await ordersRes.json();
          setOrders(ordersData);
          setFilteredOrders(ordersData);
        } else {
          console.error('Failed to fetch orders:', ordersRes.status);
        }

        // Fetch Billy Bucks balance
        const consumablesRes = await fetch(`${API_URL}/profile/consumables`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (consumablesRes.ok) {
          const consumablesData = await consumablesRes.json();
          setBillyBucks(consumablesData.billyBucks || 0);
          setOrderDuplicators(consumablesData.duplicators || {
            common: 0,
            rare: 0,
            epic: 0,
            legendary: 0
          });
        } else {
          console.error('Failed to fetch consumables:', consumablesRes.status);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch incomplete strategies when switching to completeStrategy mode
  useEffect(() => {
    if (purchaseMode === 'completeStrategy') {
      const fetchIncompleteStrategies = async () => {
        try {
          setLoadingStrategies(true);
          const token = localStorage.getItem('token');
          if (!token) return;

          const res = await fetch(`${API_URL}/bank/incomplete-strategies`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (res.ok) {
            const strategies = await res.json();
            setIncompleteStrategies(strategies);
          } else {
            console.error('Failed to fetch incomplete strategies');
          }
        } catch (err) {
          console.error('Error fetching incomplete strategies:', err);
        } finally {
          setLoadingStrategies(false);
        }
      };

      fetchIncompleteStrategies();
    } else {
      // Reset when switching back to byOrder mode
      setSelectedStrategy(null);
      setMissingOrders([]);
      setIncompleteStrategies([]);
    }
  }, [purchaseMode]);

  // Fetch missing orders when a strategy is selected
  const handleStrategySelect = async (strategy) => {
    playSoundEffect('/sounds/click.mp3');
    setSelectedStrategy(strategy);
    setSelectedOrder(null);
    setPurchaseMessage(null);

    try {
      setLoadingStrategies(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const currentLanguage = i18nInstance.language || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');

      const res = await fetch(`${API_URL}/bank/strategy/${strategy.id}/missing-orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': normalizedLanguage,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setMissingOrders(data.missingOrders || []);
      } else {
        console.error('Failed to fetch missing orders');
        setMissingOrders([]);
      }
    } catch (err) {
      console.error('Error fetching missing orders:', err);
      setMissingOrders([]);
    } finally {
      setLoadingStrategies(false);
    }
  };

  // Filter orders based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOrders(orders);
      return;
    }

    const filtered = orders.filter(order =>
      order.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOrders(filtered);
  }, [searchTerm, orders]);

  const getOrderPrice = (order) => {
    if (!order || !order.rarity) return 0;
    const rarity = order.rarity.toLowerCase();
    return RARITY_PRICES[rarity] || RARITY_PRICES.common;
  };

  const handleOrderSelect = (order) => {
    playSoundEffect('/sounds/click.mp3');
    setSelectedOrder(order);
    setPurchaseMessage(null); // Clear any previous messages
  };

  const handlePurchase = async () => {
    if (!selectedOrder) {
      setPurchaseMessage({ type: 'error', text: t('bank.selectOrderToPurchase') });
      return;
    }

    // Check if already at max copies
    if (selectedOrder.atMaxCopies) {
      const maxCount = selectedOrder.maxCount || (selectedOrder.is_unique ? 1 : 3);
      setPurchaseMessage({ 
        type: 'error', 
        text: t('bank.alreadyOwnedMax', { count: maxCount }) 
      });
      return;
    }

    const price = getOrderPrice(selectedOrder);
    if (billyBucks < price) {
      setPurchaseMessage({ type: 'error', text: t('bank.insufficientBucks') });
      return;
    }

    setPurchasing(true);
    setPurchaseMessage(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPurchaseMessage({ type: 'error', text: t('bank.mustBeLoggedInPurchase') });
        setPurchasing(false);
        return;
      }

      const res = await fetch(`${API_URL}/bank/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orderId: selectedOrder.id }),
      });

      const data = await res.json();

      if (res.ok) {
        playSoundEffect('/sounds/click.mp3');
        setBillyBucks(data.newBalance);
        
        // Refresh data based on mode
        if (purchaseMode === 'byOrder') {
          // Refresh orders list to update ownership counts
          const currentLanguage = i18nInstance.language || 'en-us';
          const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
          const ordersRes = await fetch(`${API_URL}/bank/orders?lang=${encodeURIComponent(normalizedLanguage)}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Accept-Language': normalizedLanguage,
            },
          });
          if (ordersRes.ok) {
            const ordersData = await ordersRes.json();
            setOrders(ordersData);
            setFilteredOrders(ordersData);
          }
          
          // Clear selection after successful purchase in byOrder mode
          setTimeout(() => {
            setSelectedOrder(null);
            setPurchaseMessage(null);
          }, 2000);
        } else if (purchaseMode === 'completeStrategy' && selectedStrategy) {
          // Refresh missing orders for the selected strategy
          const currentLanguage = i18nInstance.language || 'en-us';
          const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
          
          const res = await fetch(`${API_URL}/bank/strategy/${selectedStrategy.id}/missing-orders`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Accept-Language': normalizedLanguage,
            },
          });

          if (res.ok) {
            const data = await res.json();
            const updatedMissingOrders = data.missingOrders || [];
            setMissingOrders(updatedMissingOrders);
            
            // Find the currently selected order in the updated list
            const currentOrderInList = updatedMissingOrders.find(o => o.id === selectedOrder.id);
            
            if (currentOrderInList && currentOrderInList.missingCount > 0) {
              // Order still needs more copies - keep it selected
              setSelectedOrder(currentOrderInList);
            } else if (updatedMissingOrders.length > 0) {
              // Order is complete, select the next missing order
              setSelectedOrder(updatedMissingOrders[0]);
            } else {
              // All orders are complete - clear selection
              setSelectedOrder(null);
            }
          }
          
          // Clear success message after a delay
          setTimeout(() => {
            setPurchaseMessage(null);
          }, 2000);
        }
      } else {
        setPurchaseMessage({ type: 'error', text: data.error || t('bank.failedToPurchase') });
      }
    } catch (err) {
      console.error('Error purchasing order:', err);
      setPurchaseMessage({ type: 'error', text: t('bank.networkError') });
    } finally {
      setPurchasing(false);
    }
  };

  const canPurchase = selectedOrder && 
    !selectedOrder.atMaxCopies && 
    billyBucks >= getOrderPrice(selectedOrder) && 
    !purchasing;

  const handleGamble = async () => {
    if (billyBucks < 3000) {
      setGambleResult({ success: false, message: t('bank.insufficientBucksGamble') });
      return;
    }

    setGambling(true);
    setGambleResult(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setGambleResult({ success: false, message: t('bank.mustBeLoggedInGamble') });
        setGambling(false);
        return;
      }

      const res = await fetch(`${API_URL}/bank/gamble`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.ok) {
        playSoundEffect('/sounds/click.mp3');
        console.log('Gamble result:', data); // Debug log
        
        // Enrich orders with full data for display
        const enrichedPrizes = await Promise.all((data.prizes || []).map(async (prize) => {
          if (prize.type === 'order' && prize.id) {
            try {
              const orderRes = await fetch(`${API_URL}/order/${prize.id}/full`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });
              if (orderRes.ok) {
                const orderData = await orderRes.json();
                return { ...prize, orderData };
              }
            } catch (err) {
              console.error('Error fetching order data:', err);
            }
          }
          return prize;
        }));
        
        // Set both balance and result together to avoid re-render issues
        setBillyBucks(data.newBalance);
        setGambleResult({ 
          success: true, 
          message: t('bank.gambleSuccessful'),
          prizes: enrichedPrizes
        });
      } else {
        setGambleResult({ success: false, message: data.error || t('bank.failedToGamble') });
      }
    } catch (err) {
      console.error('Error gambling:', err);
      setGambleResult({ success: false, message: 'Network error. Please try again.' });
    } finally {
      setGambling(false);
    }
  };

  const handleConvertDuplicators = async (fromRarity) => {
    if (orderDuplicators[fromRarity] < 5) {
      setConvertMessage({ 
        type: 'error', 
        text: t('bank.insufficientDuplicators', { rarity: fromRarity, required: 5 }) 
      });
      return;
    }

    setConverting(true);
    setConvertMessage(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setConvertMessage({ type: 'error', text: t('bank.mustBeLoggedIn') });
        setConverting(false);
        return;
      }

      const res = await fetch(`${API_URL}/bank/convert-duplicators`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fromRarity }),
      });

      let data;
      try {
        data = await res.json();
      } catch (jsonErr) {
        console.error('Error parsing JSON response:', jsonErr);
        setConvertMessage({ 
          type: 'error', 
          text: `Server error: ${res.status} ${res.statusText}` 
        });
        return;
      }

      if (res.ok) {
        playSoundEffect('/sounds/click.mp3');
        setOrderDuplicators(data.duplicators);
        setConvertMessage({ 
          type: 'success', 
          text: data.message || t('bank.conversionSuccessful') 
        });
        
        // Clear message after delay
        setTimeout(() => {
          setConvertMessage(null);
        }, 3000);
      } else {
        setConvertMessage({ 
          type: 'error', 
          text: data.error || data.details || t('bank.conversionFailed') 
        });
      }
    } catch (err) {
      console.error('Error converting duplicators:', err);
      console.error('Error details:', err.message, err.stack);
      setConvertMessage({ 
        type: 'error', 
        text: err.message || t('bank.networkError') 
      });
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="bank-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="bank-fullscreen-background"
        style={{ backgroundImage: 'url(/images/billy_buck_bank.jpg)' }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`bank-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="bank-container">
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              playSoundEffect('/sounds/click.mp3');
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px' }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
          </div>

      <button onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        navigate('/welcome');
      }} className="back-button">
        {t('common.back')}
      </button>

      {/* Total Balance Display - Upper Middle */}
      <div className="bank-total-balance">
        <span className="bank-balance-label">{t('bank.totalBalance')}</span>
        <span className="bank-balance-value">{billyBucks.toLocaleString()} {t('bank.billyBucks')}</span>
      </div>

      {/* Bank Content */}
      <div className="bank-content">
        {/* Mode Selection */}
        <div className="bank-mode-selection">
          <div className="bank-mode-buttons">
            <button
              className={`bank-mode-button ${purchaseMode === 'byOrder' ? 'active' : ''}`}
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setPurchaseMode('byOrder');
                setSelectedOrder(null);
                setSelectedStrategy(null);
                setMissingOrders([]);
              }}
            >
              {t('bank.buyOrder')}
            </button>
            <button
              className={`bank-mode-button ${purchaseMode === 'completeStrategy' ? 'active' : ''}`}
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setPurchaseMode('completeStrategy');
                setSelectedOrder(null);
              }}
            >
              {t('bank.completeStrategy')}
            </button>
          </div>
        </div>

        {purchaseMode === 'byOrder' ? (
          <>
            {/* Search Bar */}
            <div className="bank-search-container">
              <input
                id="buy-order-search"
                type="text"
                className="bank-search-input"
                placeholder={t('bank.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Order List */}
            {loading ? (
              <div className="bank-loading">{t('bank.loadingOrders')}</div>
            ) : (
              <div className="bank-order-list">
                {filteredOrders.length === 0 ? (
                  <div className="bank-no-results">
                    {searchTerm ? t('bank.noOrdersFound', { searchTerm }) : t('bank.noOrdersAvailable')}
                  </div>
                ) : (
                  filteredOrders.map((order) => (
                    <div
                      key={order.id}
                      className={`bank-order-item ${selectedOrder?.id === order.id ? 'selected' : ''} ${order.atMaxCopies ? 'at-max' : ''}`}
                      onClick={() => handleOrderSelect(order)}
                    >
                      <span className="bank-order-name">{order.name}</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span className={`bank-order-rarity rarity-${(order.rarity || 'common').toLowerCase()}`}>{order.rarity || 'common'}</span>
                        {order.atMaxCopies && (
                          <span className="bank-order-max-indicator">
                            {t('bank.owned', { owned: order.ownedCount, max: order.maxCount })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Strategy List */}
            {loadingStrategies ? (
              <div className="bank-loading">{t('bank.loadingStrategies')}</div>
            ) : (
              <>
                {selectedStrategy ? (
                  <>
                    {/* Back to Strategy List Button */}
                    <button
                      className="bank-back-button"
                      onClick={() => {
                        playSoundEffect('/sounds/click.mp3');
                        setSelectedStrategy(null);
                        setMissingOrders([]);
                        setSelectedOrder(null);
                      }}
                    >
                      ← {t('bank.backToStrategies')}
                    </button>
                    <div className="bank-strategy-header">
                      <h3>{selectedStrategy.name}</h3>
                      {selectedStrategy.winCount > 0 && (
                        <span className="bank-strategy-wins">{t('bank.wins', { count: selectedStrategy.winCount })}</span>
                      )}
                    </div>
                    {/* Missing Orders List */}
                    <div className="bank-order-list">
                      {missingOrders.length === 0 ? (
                        <div className="bank-no-results">{t('bank.noMissingOrders')}</div>
                      ) : (
                        missingOrders.map((order) => (
                          <div
                            key={order.id}
                            className={`bank-order-item ${selectedOrder?.id === order.id ? 'selected' : ''} ${order.atMaxCopies ? 'at-max' : ''}`}
                            onClick={() => handleOrderSelect(order)}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <span className="bank-order-name">{order.name}</span>
                              <span className="bank-order-missing">
                                {t('bank.missing', { missing: order.missingCount, required: order.requiredCount, owned: order.ownedCount })}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                              <span className={`bank-order-rarity rarity-${(order.rarity || 'common').toLowerCase()}`}>{order.rarity || 'common'}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Spacer to match search bar height in buy order mode */}
                    <div className="bank-search-container" style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                      <input type="text" className="bank-search-input" />
                    </div>
                    <div className="bank-order-list">
                      {incompleteStrategies.length === 0 ? (
                        <div className="bank-no-results">{t('bank.noIncompleteStrategies')}</div>
                      ) : (
                        incompleteStrategies.map((strategy) => (
                          <div
                            key={strategy.id}
                            className={`bank-order-item ${selectedStrategy?.id === strategy.id ? 'selected' : ''}`}
                            onClick={() => handleStrategySelect(strategy)}
                          >
                            <span className="bank-order-name">{strategy.name}</span>
                            {strategy.winCount > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <span className="bank-strategy-wins">{t('bank.wins', { count: strategy.winCount })}</span>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* Total Cost */}
        {purchaseMode === 'byOrder' || (purchaseMode === 'completeStrategy' && selectedStrategy) ? (
          <div className="bank-cost-section">
            <div className="bank-cost-line">
              <span className="bank-cost-label">{t('bank.totalCost')}</span>
              <span className="bank-cost-value">
                {selectedOrder ? getOrderPrice(selectedOrder).toLocaleString() : '0'} {t('bank.billyBucks')}
              </span>
            </div>
          </div>
        ) : null}

        {/* Purchase Message */}
        {purchaseMessage && (
          <div className={`bank-message ${purchaseMessage.type}`}>
            {purchaseMessage.text}
          </div>
        )}

        {/* Max Copies Warning */}
        {selectedOrder && selectedOrder.atMaxCopies && (
          <div className="bank-message error">
            {t('bank.alreadyOwnedMax', { count: selectedOrder.maxCount })}
          </div>
        )}

        {/* Buy Button */}
        {(purchaseMode === 'byOrder' || (purchaseMode === 'completeStrategy' && selectedStrategy)) && (
          <button
            className="bank-buy-button"
            onClick={handlePurchase}
            disabled={!canPurchase}
          >
            {purchasing ? t('bank.processing') : selectedOrder?.atMaxCopies ? t('bank.alreadyOwnedMaxButton') : t('bank.buy')}
          </button>
        )}
      </div>

      {/* Duplicator Conversion Section */}
      <div className="bank-conversion-content">
        <div className="bank-conversion-header">
          <h2 className="bank-conversion-title">{t('bank.convertDuplicators')}</h2>
        </div>
        
        <div className="bank-conversion-grid">
          {/* Common to Rare */}
          <div className="bank-conversion-item">
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-common">{t('bank.common')}</div>
              <div className="bank-conversion-count">{orderDuplicators.common}</div>
            </div>
            <div className="bank-conversion-arrow">→</div>
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-rare">{t('bank.rare')}</div>
              <div className="bank-conversion-rate">5 → 1</div>
            </div>
            <button
              className="bank-convert-button"
              onClick={() => handleConvertDuplicators('common')}
              disabled={orderDuplicators.common < 5 || converting}
            >
              {converting ? t('bank.converting') : t('bank.convert')}
            </button>
          </div>

          {/* Rare to Epic */}
          <div className="bank-conversion-item">
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-rare">{t('bank.rare')}</div>
              <div className="bank-conversion-count">{orderDuplicators.rare}</div>
            </div>
            <div className="bank-conversion-arrow">→</div>
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-epic">{t('bank.epic')}</div>
              <div className="bank-conversion-rate">5 → 1</div>
            </div>
            <button
              className="bank-convert-button"
              onClick={() => handleConvertDuplicators('rare')}
              disabled={orderDuplicators.rare < 5 || converting}
            >
              {converting ? t('bank.converting') : t('bank.convert')}
            </button>
          </div>

          {/* Epic to Legendary */}
          <div className="bank-conversion-item">
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-epic">{t('bank.epic')}</div>
              <div className="bank-conversion-count">{orderDuplicators.epic}</div>
            </div>
            <div className="bank-conversion-arrow">→</div>
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-legendary">{t('bank.legendary')}</div>
              <div className="bank-conversion-rate">5 → 1</div>
            </div>
            <button
              className="bank-convert-button"
              onClick={() => handleConvertDuplicators('epic')}
              disabled={orderDuplicators.epic < 5 || converting}
            >
              {converting ? t('bank.converting') : t('bank.convert')}
            </button>
          </div>
        </div>

        {/* Conversion Message */}
        {convertMessage && (
          <div className={`bank-message ${convertMessage.type}`} style={{ marginTop: '15px' }}>
            {convertMessage.text}
          </div>
        )}
      </div>

      {/* Gamble Box */}
      <div className="bank-gamble-content">
        <div className="bank-gamble-header">
          <h2 className="bank-gamble-title">{t('bank.gamble')}</h2>
        </div>
        
        <button
          className="bank-gamble-button"
          onClick={handleGamble}
          disabled={billyBucks < 3000 || gambling}
        >
          {gambling ? t('bank.rolling') : t('bank.betButton')}
        </button>

        {gambleResult && (
          <div className="bank-gamble-result">
            {!gambleResult.success && (
              <div className={`bank-message error`}>
                {gambleResult.message}
              </div>
            )}
            {gambleResult.prizes && gambleResult.prizes.length > 0 ? (
              <div className="bank-gamble-prizes">
                <h3>{t('bank.prizesWon')}</h3>
                <div className="bank-gamble-prizes-grid">
                  {gambleResult.prizes.map((prize, index) => (
                    <div key={index} className="bank-gamble-prize-card">
                      {prize.type === 'billy_bucks' && (
                        <div className="bank-prize-billy-bucks">
                          <img 
                            src="/images/billy_bucks.png" 
                            alt="Billy Bucks" 
                            className="bank-prize-image"
                          />
                          <div className="bank-prize-amount">{prize.amount || 0}</div>
                          <div className="bank-prize-label">{t('bank.billyBucks')}</div>
                        </div>
                      )}
                      {prize.type === 'order' && prize.orderData && (
                        <div className={`reward-order-card rarity-${(prize.orderData.rarity || prize.rarity || 'common').toLowerCase()}`}>
                          <OrderDesign
                            order={prize.orderData}
                            minimal={false}
                            noMargin={true}
                          />
                        </div>
                      )}
                      {prize.type === 'order' && !prize.orderData && (
                        <div className="bank-prize-fallback">
                          📜 {prize.name || t('bank.unknownOrder')} ({prize.rarity || t('bank.unknown')})
                        </div>
                      )}
                      {prize.type === 'duplicator' && (
                        <div className="bank-prize-duplicator">
                          <div className="bank-prize-duplicator-icon">🔁</div>
                          <div className="bank-prize-name">{prize.name || t('bank.unknownDuplicator')}</div>
                          <div className="bank-prize-rarity">{prize.rarity || t('bank.unknown')}</div>
                        </div>
                      )}
                      {prize.type === 'preparation_bonus' && (() => {
                        // Match by key - same as Profile.js
                        const feature = preparationBonusFeatures.find(f => f.key === prize.key);
                        const imageSrc = feature ? feature.image : '/images/team_leader_briefing.jpg'; // Fallback
                        return (
                          <div className="bank-prize-feature">
                            <img 
                              src={imageSrc} 
                              alt={feature?.alt || 'Preparation Bonus'} 
                              className="preparation-bonus-image"
                              style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                            />
                            <div className="bank-prize-label">{t('bank.preparationBonus')}</div>
                            <div className="bank-prize-name">{prize.name || prize.key || t('bank.unknown')}</div>
                          </div>
                        );
                      })()}
                      {prize.type === 'diplomatic_accord' && (() => {
                        // Match by key - same as Profile.js
                        const prizeKey = prize.key;
                        console.log('Diplomatic accord prize:', prize, 'Looking for key:', prizeKey, 'Type:', typeof prizeKey);
                        console.log('Available features:', diplomaticAccordsFeatures.map(f => ({ key: f.key, type: typeof f.key })));
                        const feature = diplomaticAccordsFeatures.find(f => {
                          const match = f.key === prizeKey;
                          console.log(`Comparing "${f.key}" === "${prizeKey}": ${match}`);
                          return match;
                        });
                        console.log('Found feature:', feature);
                        if (!feature) {
                          console.error('No feature found for diplomatic_accord key:', prizeKey, 'Available keys:', diplomaticAccordsFeatures.map(f => f.key));
                        }
                        const imageSrc = feature ? feature.image : '/images/strategic_coordination.jpg'; // Fallback
                        console.log('Using image:', imageSrc);
                        return (
                          <div className="bank-prize-feature">
                            <img 
                              src={imageSrc} 
                              alt={feature?.alt || 'Diplomatic Accord'} 
                              className="preparation-bonus-image"
                              style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                              onError={(e) => {
                                console.error('Image failed to load:', imageSrc);
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('Image loaded successfully:', imageSrc);
                              }}
                            />
                            <div className="bank-prize-label">{t('bank.diplomaticAccord')}</div>
                            <div className="bank-prize-name">{prize.name || prize.key || t('bank.unknown')}</div>
                            {!feature && <div style={{ color: 'red', fontSize: '0.8rem' }}>Key: {prizeKey}</div>}
                          </div>
                        );
                      })()}
                      {prize.type === 'avatar' && (
                        <div className="bank-prize-fallback">
                          🖼️ {t('bank.avatar')} {prize.name || prize.unlockKey || t('bank.unknown')}
                        </div>
                      )}
                      {prize.type === 'nothing' && (
                        <div className="bank-prize-nothing">
                          <div className="bank-prize-nothing-icon">❌</div>
                          <div className="bank-prize-label">{t('bank.nothing')}</div>
                          <div className="bank-prize-name">{t('bank.betterLuckNextTime')}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : gambleResult.success && (
              <div className="bank-message error" style={{ marginTop: '10px' }}>
                {t('bank.noPrizesGranted')}
              </div>
            )}
          </div>
        )}
      </div>

      {showTutorialBank && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_welcome_3.png"
          text={t('bank.tutorialText')}
          onContinue={async () => {
            const token = localStorage.getItem('token');
            if (token) {
              await fetch(`${API_URL}/tutorial/mark-seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ messageKey: 'bank' }),
              });
              setSeenTutorialMessages(prev => prev.includes('bank') ? prev : [...prev, 'bank']);
            }
            setShowTutorialBank(false);
          }}
        />,
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default Bank;

